import { computed, toRaw, watch } from "vue";
import { defineStore } from "pinia";
import { computedAsync, identity, until, useEventListener } from "@vueuse/core";

import { addBreadcrumb } from "@sentry/vue";

import { recommendedVoice } from "@/helpers/types";
import { useReadAloudVoices } from "@/services/composables/useReadAloudVoices";
import { dynamicSort, getDebugMode } from "@/helpers/helperFunctions";

import { usePublicationStore } from "./publication";
import { useSettingsStore } from "./settings";
import { useWpsStore } from "./wps";

import TTSEngine from "@/services/tts/TTSEngine";
import { ttsLanguageOptions } from "@/helpers/constants";
import { invokeAsync } from "@/helpers/async";

import { LanguageTag } from "../../modules/shared-utils/languageTag";

export const useTTSStore = defineStore("tts", () => {
  const settingsStore = useSettingsStore();

  const { getRecommendedVoices, getSpeechSynthesisVoices } =
    useReadAloudVoices();

  const recommendedVoices = computedAsync<recommendedVoice[]>(
    () => getRecommendedVoices(),
    []
  );

  const availableVoices = computedAsync<SpeechSynthesisVoice[]>(
    async () => (await getSpeechSynthesisVoices()).sort(dynamicSort("lang")),
    []
  );

  const availableLanguages = computed(() => {
    const languages = new Set<string>();
    availableVoices.value.forEach(({ lang }) => {
      languages.add(LanguageTag.parse(lang).primary);
    });

    return languages;
  });

  const voicesForLanguage = computed<SpeechSynthesisVoice[]>((oldValues) => {
    const lang = settingsStore.perContent.ttsLang;
    const voices = availableVoices.value.filter((voice) => {
      return voice.lang.startsWith(lang);
    });

    return voices.length ? voices : (oldValues ?? []);
  });

  const selectedVoice = computed<SpeechSynthesisVoice>(() => {
    const ttsVoice = settingsStore.perContent.ttsVoice;
    if (!ttsVoice) return;
    return availableVoices.value.find((voice) => voice.name === ttsVoice);
  });

  const publicationStore = usePublicationStore();
  const wpsStore = useWpsStore();

  function toTtsLanguageOption(lang: string | null): string | null {
    if (!lang) return null;

    const ttsLanguage = ttsLanguageOptions.find((l) => l.value === lang);
    if (ttsLanguage) {
      return lang;
    }
    lang = LanguageTag.parse(lang).primary;
    return ttsLanguageOptions.find((l) => l.value === lang)?.value;
  }

  const languageSources = [
    // we don't want type based settings to override the language,
    // it should come only first from this specific publication's settings
    () => toTtsLanguageOption(publicationStore.storage.settings.ttsLang),
    // if no tts language was previously chosen for that publication,
    // try to determine it from the publication metadata
    () =>
      wpsStore.client.publication.manifest.metadata.languages
        ?.map(toTtsLanguageOption)
        .filter(identity)[0],
    // fallback to the token language
    () => toTtsLanguageOption(wpsStore.client.token.language),
    // finally, fallback to the browser's configured languages
    () =>
      (window.navigator.languages ?? [window.navigator.language])
        .map(toTtsLanguageOption)
        .filter(identity)[0],
  ];

  // initialize default language and voices on store creation
  invokeAsync(async () => {
    await until(() => publicationStore.ready).toBe(true);

    for (const source of languageSources) {
      const lang = source();
      if (lang) {
        publicationStore.storage.settings.ttsLang = lang;
        return;
      }
    }

    // final fallback to english
    publicationStore.storage.settings.ttsLang = "en";
  });

  const setRecommendedVoiceTTS = (): void => {
    let voice: SpeechSynthesisVoice = null;
    const browserLanguages = window.navigator?.languages;

    if (getDebugMode())
      console.log("available voices: ", voicesForLanguage.value);

    // If the current voice is already in the list of voices for the current language, we do nothing
    if (
      voicesForLanguage.value.some(
        (voice) => voice.name === selectedVoice.value?.name
      )
    )
      return;

    // Search which voices in the current language list are also listed on the recommended voices list
    const recommendedVoicesList = voicesForLanguage.value?.filter((voice) =>
      recommendedVoices.value?.some((rVoice) => rVoice.name === voice.name)
    );

    // Sort the voices list based on the recommended voices list order
    recommendedVoicesList?.sort((a, b) => {
      return (
        recommendedVoices.value?.findIndex((rVoice) => rVoice.name === a.name) -
        recommendedVoices.value?.findIndex((rVoice) => rVoice.name === b.name)
      );
    });

    // Find more accurated voice to use based on the browser languages priority
    let chosenVoice: SpeechSynthesisVoice = null;
    const foundMatch = browserLanguages.find((lang) => {
      const browserTag = LanguageTag.parse(lang);

      const foundVoiceExactMatch = recommendedVoicesList?.find(
        (voice) =>
          voice.lang != null && LanguageTag.parse(voice.lang).match(browserTag)
      );

      if (foundVoiceExactMatch) {
        chosenVoice = foundVoiceExactMatch;
      } else {
        const foundVoiceMatch = recommendedVoicesList?.find(
          (voice) =>
            voice.lang != null &&
            LanguageTag.parse(voice.lang).primary === browserTag.primary
        );

        if (foundVoiceMatch) {
          chosenVoice = foundVoiceMatch;
        }
        return foundVoiceMatch;
      }
      return foundVoiceExactMatch;
    });

    // If there is a match, update the current language value
    if (foundMatch) {
      ttsLanguageOptions.find((lang) => {
        if (lang.value === LanguageTag.parse(foundMatch).primary) {
          settingsStore.perContent.ttsLang = lang.value;
        }
      });
    } else {
      // If there is no match, we use the first voice of the filtered voices list
      chosenVoice = voicesForLanguage.value?.[0];
    }

    voice = chosenVoice;
    if (voice) {
      settingsStore.perContent.ttsVoice = voice.name;

      const voiceRateValue = recommendedVoices.value?.find(
        (voiceTTS) => voiceTTS.name === voice.name
      )?.rate;
      const voicePitchValue = recommendedVoices.value?.find(
        (voiceTTS) => voiceTTS.name === voice.name
      )?.pitch;

      if (voiceRateValue)
        settingsStore.perContent.ttsVoiceSpeed = voiceRateValue;
      if (voicePitchValue)
        settingsStore.perContent.ttsVoicePitch = voicePitchValue;
    }
  };

  watch(
    [selectedVoice, recommendedVoices],
    ([voice, recommendedVoices]: [
      SpeechSynthesisVoice,
      recommendedVoice[],
    ]) => {
      if (!voice) {
        return;
      }
      const recommendedVoice = recommendedVoices.find(
        (recommended) => recommended.name === voice.name
      );
      if (recommendedVoice) {
        settingsStore.perContent.ttsVoicePitch = recommendedVoice.pitch;
        settingsStore.perContent.ttsVoiceSpeed = recommendedVoice.rate;
      }
    }
  );

  watch(voicesForLanguage, (voices) => {
    if (voices.length === 0) {
      console.warn("No voices available for the selected language");
      return;
    }

    // If the current voice is not in the current language voices list, we search for a recommended voice
    if (!voices.some((voice) => voice.name === selectedVoice.value?.name)) {
      setRecommendedVoiceTTS();
    }
  });

  // prevent TTS to continue speaking after a page reload
  useEventListener(window, "beforeunload", () => TTSEngine.stop());

  let rawConfigValues: {
    voice: SpeechSynthesisVoice | null;
    lang: string | null;
    rate: number;
    pitch: number;
    volume: number;
  } = {
    voice: null,
    lang: null,
    rate: 1,
    pitch: 1,
    volume: 0.5,
  };

  watch(
    () => {
      const { ttsVoicePitch, ttsVoiceSpeed, ttsVoiceVolume } =
        settingsStore.perContent;

      // we want raw values for all the calls to configure
      // they don't need to react to reactivity
      const voice = toRaw(selectedVoice.value);

      let volume = ttsVoiceVolume / 100 || 0.5;
      if (volume < 0.03) {
        volume = 0;
      }

      return {
        voice,
        lang: voice?.lang,
        rate: ttsVoiceSpeed || 1,
        pitch: ttsVoicePitch || 1,
        volume,
      };
    },
    (values) => (rawConfigValues = values)
  );

  let voiceNotSetErrorLogged = false;

  return {
    recommendedVoices,
    availableLanguages,
    voicesForLanguage,
    configure(utterance: SpeechSynthesisUtterance) {
      if (!rawConfigValues.voice && !voiceNotSetErrorLogged) {
        voiceNotSetErrorLogged = true;
        addBreadcrumb({
          category: "tts",
          message: "TTS voice is not set",
          level: "warning",
        });
      }

      Object.assign(utterance, rawConfigValues);
    },
  };
});
